// Temperature-history helpers — ported from the b-coldtrack `inout-logs/[id]`
// view (downsample + breach detection + stats). corntrol is frontend-only, so
// the computation the b-coldtrack backend did (stats, breach grouping) runs
// here on the raw records the temperature report already returns.
//
// These are pure functions: no React, no chart lib. `downsample` is generic
// over the full record so every sensor field is carried through to the chart;
// `computeStats` / `detectBreaches` only need { time, value }.

import dayjs from "dayjs";

export interface Threshold {
  min: number | null;
  max: number | null;
}

export interface TempStat {
  min: number | null;
  max: number | null;
  avg: number | null;
  count: number;
}

export type BreachType = "LOW" | "HIGH" | "BOTH";

export interface BreachEvent {
  start: string;
  end: string;
  min: number;
  max: number;
  count: number;
  type: BreachType;
  // The thresholds that were actually crossed, paired with the extremes above:
  // limitMin belongs to the lowest reading, limitMax to the highest. Set by the
  // dynamic detector only — with per-record thresholds, a reader can't tell why
  // e.g. 7.2°C is a breach unless the report shows the limit in force then.
  limitMin?: number | null;
  limitMax?: number | null;
}

// The minimum a reading needs for stats/breach: a timestamp + the value to
// evaluate. Callers map their record's primary sensor into `value`.
export interface ValuedReading {
  time: string;
  value: number;
}

const toMs = (time: string): number => new Date(time).getTime();

// Spread-based Math.min/max/reduce overflow the call stack past ~100k items
// (months of minute data), so fold instead.
function foldMinMaxSum(values: number[]): { min: number; max: number; sum: number } {
  let min = Infinity;
  let max = -Infinity;
  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (v < min) min = v;
    if (v > max) max = v;
    sum += v;
  }
  return { min, max, sum };
}

export function isBreachValue(value: number, threshold: Threshold): boolean {
  return (
    (threshold.min !== null && value < threshold.min) ||
    (threshold.max !== null && value > threshold.max)
  );
}

export function computeStats(readings: ValuedReading[]): TempStat {
  if (readings.length === 0) {
    return { min: null, max: null, avg: null, count: 0 };
  }
  const { min, max, sum } = foldMinMaxSum(readings.map((r) => r.value));
  return {
    min,
    max,
    avg: Math.round((sum / readings.length) * 100) / 100,
    count: readings.length,
  };
}

// Group consecutive out-of-range readings into breach events. Expects readings
// sorted ascending by time (the report already returns them in order). Each
// event closes at the first in-range reading's timestamp; a still-open breach
// at the end closes at the last reading's timestamp. Mirrors the b-coldtrack
// backend grouping (inout-logs.service.ts).
export function detectBreaches(
  readings: ValuedReading[],
  threshold: Threshold
): BreachEvent[] {
  const events: BreachEvent[] = [];
  if (threshold.min === null && threshold.max === null) return events;

  let inBreach = false;
  let start = "";
  let values: number[] = [];

  const closeEvent = (endTime: string) => {
    const { min, max } = foldMinMaxSum(values);
    const belowMin = threshold.min !== null && min < threshold.min;
    const aboveMax = threshold.max !== null && max > threshold.max;
    const type: BreachType = belowMin && aboveMax ? "BOTH" : belowMin ? "LOW" : "HIGH";
    events.push({ start, end: endTime, min, max, count: values.length, type });
    inBreach = false;
    values = [];
  };

  for (const r of readings) {
    if (isBreachValue(r.value, threshold)) {
      if (!inBreach) {
        inBreach = true;
        start = r.time;
        values = [r.value];
      } else {
        values.push(r.value);
      }
    } else if (inBreach) {
      closeEvent(r.time);
    }
  }
  if (inBreach && values.length > 0 && readings.length > 0) {
    closeEvent(readings[readings.length - 1].time);
  }
  return events;
}

// Per-record threshold variant. The alarm/set-point bounds can change over time
// (the operator edits them on the device), so a single global threshold would
// mis-count breaches after a change. Here each reading carries the min/max in
// effect at its own timestamp, and breach is evaluated against those.
export interface DynamicReading {
  time: string;
  value: number;
  min: number | null; // threshold-low in effect at this time
  max: number | null; // threshold-high in effect at this time
}

export function isBreachDynamic(r: {
  value: number;
  min: number | null;
  max: number | null;
}): boolean {
  return (
    (r.min !== null && r.value < r.min) || (r.max !== null && r.value > r.max)
  );
}

export function detectBreachesDynamic(readings: DynamicReading[]): BreachEvent[] {
  const events: BreachEvent[] = [];
  let inBreach = false;
  let start = "";
  let values: number[] = [];
  let sawLow = false;
  let sawHigh = false;
  // thresholds belonging to the extremes — captured from the same reading that
  // set each extreme, so the pair (value, limit) in the report always describes
  // one moment even if the operator edits the bounds mid-excursion
  let limitLow: number | null = null;
  let limitHigh: number | null = null;
  let lowest = Infinity;
  let highest = -Infinity;

  const close = (endTime: string) => {
    const { min, max } = foldMinMaxSum(values);
    const type: BreachType = sawLow && sawHigh ? "BOTH" : sawLow ? "LOW" : "HIGH";
    events.push({
      start,
      end: endTime,
      min,
      max,
      count: values.length,
      type,
      limitMin: limitLow,
      limitMax: limitHigh,
    });
    inBreach = false;
    values = [];
    sawLow = false;
    sawHigh = false;
    limitLow = null;
    limitHigh = null;
    lowest = Infinity;
    highest = -Infinity;
  };

  for (const r of readings) {
    const below = r.min !== null && r.value < r.min;
    const above = r.max !== null && r.value > r.max;
    if (below || above) {
      if (!inBreach) {
        inBreach = true;
        start = r.time;
        values = [r.value];
        sawLow = below;
        sawHigh = above;
      } else {
        values.push(r.value);
        if (below) sawLow = true;
        if (above) sawHigh = true;
      }
      if (below && r.value < lowest) {
        lowest = r.value;
        limitLow = r.min;
      }
      if (above && r.value > highest) {
        highest = r.value;
        limitHigh = r.max;
      }
    } else if (inBreach) {
      close(r.time);
    }
  }
  if (inBreach && values.length > 0 && readings.length > 0) {
    close(readings[readings.length - 1].time);
  }
  return events;
}

// ── Alarm-window severity ────────────────────────────────────────────────────
// The device delays its temperature alarm by `timeAlertDelay` seconds and
// posts activity-log markers the moment the alarm ACTUALLY fires (tHi/tLo = 1)
// and clears (= 0). Severity is modeled as ACTIVE WINDOWS between those
// markers: a breaching reading inside a matching-direction window is a true
// "alarm" (red); breaching readings outside — the within-delay lead-in of an
// excursion, or after the alarm cleared — are "brief" (soft amber).
export type BreachSeverity = "alarm" | "brief";

export interface AlarmLog {
  time: string;
  key: "tHi" | "tLo";
  on: boolean; // true = alarm fired (value 1), false = cleared (value 0)
}

export interface AlarmWindow {
  key: "tHi" | "tLo";
  start: number; // ms epoch
  end: number; // ms epoch; Infinity when still active at the end of the logs
}

// Pair fired/cleared markers per direction into active windows. Logs may
// arrive in any order; an unclosed alarm stays open to Infinity.
export function buildAlarmWindows(logs: AlarmLog[]): AlarmWindow[] {
  const windows: AlarmWindow[] = [];
  const open: Partial<Record<"tHi" | "tLo", number>> = {};
  const sorted = [...logs].sort((a, b) => toMs(a.time) - toMs(b.time));
  for (const l of sorted) {
    const t = toMs(l.time);
    if (l.on) {
      if (open[l.key] === undefined) open[l.key] = t;
    } else if (open[l.key] !== undefined) {
      windows.push({ key: l.key, start: open[l.key] as number, end: t });
      delete open[l.key];
    }
  }
  for (const k of ["tHi", "tLo"] as const) {
    if (open[k] !== undefined) {
      windows.push({ key: k, start: open[k] as number, end: Infinity });
    }
  }
  return windows;
}

// Fallback when the activity-log fetch failed: reconstruct the windows the
// device WOULD have produced — its alarm fires `alertDelaySec` after an
// excursion starts and clears when the excursion ends. Delay 0/absent = the
// alarm is immediate, so the window covers the whole event (previous report
// behavior). An excursion shorter than the delay produces no window (brief).
export function heuristicAlarmWindows(
  events: BreachEvent[],
  alertDelaySec: number | null | undefined
): AlarmWindow[] {
  const delayMs = Math.max(0, alertDelaySec ?? 0) * 1000;
  const out: AlarmWindow[] = [];
  for (const e of events) {
    const start = toMs(e.start) + delayMs;
    const end = toMs(e.end);
    if (start > end) continue; // returned in-range before the delay elapsed
    const keys: ("tHi" | "tLo")[] =
      e.type === "BOTH" ? ["tHi", "tLo"] : e.type === "HIGH" ? ["tHi"] : ["tLo"];
    for (const key of keys) out.push({ key, start, end });
  }
  return out;
}

// Event-level severity (breach table / banner counts): a true alarm iff a
// matching-direction window overlaps the event (±tolSec for clock skew).
export function classifyBreachByWindows(
  e: BreachEvent,
  windows: AlarmWindow[],
  tolSec = 60
): BreachSeverity {
  const from = toMs(e.start) - tolSec * 1000;
  const to = toMs(e.end) + tolSec * 1000;
  const wantHigh = e.type === "HIGH" || e.type === "BOTH";
  const wantLow = e.type === "LOW" || e.type === "BOTH";
  for (const w of windows) {
    if (w.key === "tHi" && !wantHigh) continue;
    if (w.key === "tLo" && !wantLow) continue;
    if (w.start <= to && w.end >= from) return "alarm";
  }
  return "brief";
}

// Row-level severity for ONE breaching reading: red only while the device
// alarm was actually active. `spanMs` widens the check for averaged rows
// stamped with their window-start time; `dir` narrows to the direction the
// row actually breached ("any" when unknown).
export function rowAlarmSeverity(
  timeMs: number,
  spanMs: number,
  dir: "high" | "low" | "any",
  windows: AlarmWindow[]
): BreachSeverity {
  for (const w of windows) {
    if (dir === "high" && w.key !== "tHi") continue;
    if (dir === "low" && w.key !== "tLo") continue;
    if (w.start <= timeMs + spanMs && w.end >= timeMs) return "alarm";
  }
  return "brief";
}

// Downsample to one reading per `intervalMin`, clock-aligned, keeping the
// reading nearest each target (within half an interval). Generic so the full
// record (every sensor) is preserved for the chart. Breach-aware: any reading
// matching `opts.isBreach` that sampling dropped is merged back so no violation
// disappears. `intervalMin <= 1` returns the input unchanged.
export function downsample<T extends { time: string }>(
  readings: T[],
  intervalMin: number,
  opts?: { isBreach?: (r: T) => boolean; getKey?: (r: T) => string }
): T[] {
  if (intervalMin <= 1 || readings.length === 0) return readings;

  const intervalMs = intervalMin * 60_000;
  const sorted = [...readings].sort((a, b) => toMs(a.time) - toMs(b.time));
  const firstMs = toMs(sorted[0].time);
  const lastMs = toMs(sorted[sorted.length - 1].time);
  const startTarget = Math.ceil(firstMs / intervalMs) * intervalMs;

  const result: T[] = [];
  let i = 0;
  for (let target = startTarget; target <= lastMs; target += intervalMs) {
    while (
      i < sorted.length - 1 &&
      Math.abs(toMs(sorted[i + 1].time) - target) <=
        Math.abs(toMs(sorted[i].time) - target)
    ) {
      i++;
    }
    const nearest = sorted[i];
    if (Math.abs(toMs(nearest.time) - target) <= intervalMs / 2) {
      result.push(nearest);
    }
  }

  const isBreach = opts?.isBreach;
  if (isBreach) {
    const getKey = opts?.getKey ?? ((r: T) => r.time);
    const present = new Set(result.map(getKey));
    const missedBreaches = sorted.filter(
      (r) => isBreach(r) && !present.has(getKey(r))
    );
    if (missedBreaches.length > 0) {
      result.push(...missedBreaches);
      result.sort((a, b) => toMs(a.time) - toMs(b.time));
    }
  }
  return result;
}

const toNumOrNull = (v: any): number | null => {
  if (v === null || v === undefined || v === "-" || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

// Average-per-window downsample — the on-page chart/table "data resolution".
// Readings are grouped into clock-aligned windows of `intervalMin` minutes and
// each `numericKeys` field is AVERAGED over the samples ACTUALLY present in the
// window, dividing by that count — not by the nominal window size. This is the
// methodology cold-chain engineers accept as credible: a 5-min window holding
// only 4 samples divides by 4, not 5, so a partial window stays trustworthy.
//
// A window with no sample for a field yields `null` there, so the reading is a
// real gap — the chart breaks (missing data is visible) and the table renders
// "-". Because the source already comes as a null-filled 1-minute grid (see
// utils/reportAggregation), an empty minute survives as a null row: at 1-min
// resolution every gap shows; at coarser resolutions a fully-empty window shows.
//
// Out-of-range readings are never averaged away. Two mechanisms, per caller:
// - `opts.breachItemize` (preferred — owner's spec): every raw reading whose
//   governed key breaches ITS OWN row bounds (minKey/maxKey) is merged back
//   VERBATIM as its own row, so the table itemizes each violation point. To
//   avoid double-reporting, the window rows average only the IN-RANGE samples
//   of the governed keys (a breaching sample lives in its own row instead);
//   other keys average all samples. A window whose governed samples ALL breach
//   yields null ("-") there — the itemized rows adjacent carry the story.
// - `opts.isBreach` (legacy): breaching raw readings are merged back as extra
//   rows, but window averages still include them.
// Threshold fields (set-point / alarm bounds) are averaged like any other
// series; they are step values that rarely change intra-window, so this only
// smooths them cosmetically — breach counting elsewhere uses raw records.
export function downsampleAvg<T extends { time: string }>(
  readings: T[],
  intervalMin: number,
  numericKeys: string[],
  opts?: {
    isBreach?: (r: T) => boolean;
    getKey?: (r: T) => string;
    breachItemize?: { keys: string[]; minKey: string; maxKey: string };
  }
): T[] {
  if (readings.length === 0) return readings;
  const stepMin = Math.max(1, Math.floor(intervalMin));
  const intervalMs = stepMin * 60_000;
  const sorted = [...readings].sort((a, b) => toMs(a.time) - toMs(b.time));
  // 1-minute resolution = the source grid itself: nothing to average and
  // nothing to itemize — return the raw rows verbatim. (Bucketing here would
  // also blank single-sample breach windows under breachItemize: the sample is
  // excluded from its own average and the re-merge collides on the identical
  // minute stamp.)
  if (stepMin <= 1) return sorted;

  // Bucket by clock-aligned window start (ms). `order` keeps first-seen order so
  // the output stays chronological without a second sort of the averaged rows.
  const order: number[] = [];
  const buckets = new Map<number, T[]>();
  for (const r of sorted) {
    const start = Math.floor(toMs(r.time) / intervalMs) * intervalMs;
    let arr = buckets.get(start);
    if (!arr) {
      arr = [];
      buckets.set(start, arr);
      order.push(start);
    }
    arr.push(r);
  }

  // Does this raw row's `key` breach the row's OWN bounds? (bounds can change
  // mid-window — each sample is judged against the values in effect at its time)
  const bi = opts?.breachItemize;
  const keyBreaches = (row: any, key: string): boolean => {
    if (!bi || !bi.keys.includes(key)) return false;
    const v = toNumOrNull(row[key]);
    if (v === null) return false;
    const lo = toNumOrNull(row[bi.minKey]);
    const hi = toNumOrNull(row[bi.maxKey]);
    return (lo !== null && v < lo) || (hi !== null && v > hi);
  };

  const result: T[] = [];
  for (const start of order) {
    const rows = buckets.get(start) as T[];
    // Carry non-numeric fields from the first row; stamp the window-start time
    // (distinct from the raw per-minute timestamps) so breach re-merge below can
    // re-add the raw breaching reading without colliding with this averaged row.
    const rep: any = { ...rows[0], time: dayjs(start).format("YYYY-MM-DD HH:mm:ss") };
    let hadBreach = false;
    for (const key of numericKeys) {
      let sum = 0;
      let count = 0;
      for (const row of rows) {
        const v = toNumOrNull((row as any)[key]);
        // breachItemize: governed samples that breach are excluded from the
        // window mean — they appear verbatim as their own rows instead
        if (v !== null && keyBreaches(row, key)) {
          hadBreach = true;
          continue;
        }
        if (v !== null) {
          sum += v;
          count++;
        }
      }
      // Divide by the actual sample count (engineer's rule); null when the
      // window carried no reading for this field, so the gap is preserved.
      rep[key] = count > 0 ? Math.round((sum / count) * 100) / 100 : null;
    }
    // A window whose governed samples ALL breached has nothing left to
    // average — the itemized rows below tell the whole story. Emitting the
    // rep too would print a "-" row sharing the window-start stamp with a
    // real reading ("-" must mean NO DATA, never "all readings breached").
    // A window with no breach and no data still emits its rep, keeping the
    // genuine gap visible.
    const allGovernedNull = bi
      ? bi.keys.every((k) => rep[k] === null || rep[k] === undefined)
      : false;
    if (!(bi && hadBreach && allGovernedNull)) {
      if (bi && hadBreach) {
        // The mean describes only the IN-RANGE samples, so stamp the rep with
        // the first sample that actually contributed — e.g. a 07:45–07:49
        // window whose 07:45–07:46 breached averages 07:47–49 and is stamped
        // 07:47. This is more truthful AND cannot collide with the itemized
        // breach rows (a sample is either itemized or contributing, never
        // both), which previously produced two rows sharing "07:45".
        const firstContrib = rows.find(
          (row) =>
            !bi.keys.some((k) => keyBreaches(row, k)) &&
            bi.keys.some((k) => toNumOrNull((row as any)[k]) !== null)
        );
        if (firstContrib) rep.time = firstContrib.time;
      }
      result.push(rep as T);
    }
  }

  const isBreach = opts?.isBreach;
  if (isBreach || bi) {
    const getKey = opts?.getKey ?? ((r: T) => r.time);
    const present = new Set(result.map(getKey));
    // breachItemize rows are ALWAYS added — the averaged rep never carries
    // their values (they were excluded from the mean), so a presence check on
    // the shared window-start minute would silently drop the violation.
    // Legacy isBreach rows keep the presence check (the rep includes them).
    const missed = sorted.filter((r) =>
      bi && bi.keys.some((k) => keyBreaches(r, k))
        ? true
        : isBreach
        ? isBreach(r) && !present.has(getKey(r))
        : false
    );
    if (missed.length > 0) {
      result.push(...missed);
      result.sort((a, b) => toMs(a.time) - toMs(b.time));
    }
  }
  return result;
}

// Recommended data-resolution (minutes) for each report period unit, chosen so
// the chart stays light and readable: an hour/day of minute data is fine at
// 1-min, a week at 10-min, a month at 30-min. This is ALSO the finest allowed
// per range — the filter modal only offers this value and coarser (a month at
// 1-min would be ~35k rows that freeze the chart). Out-of-range readings are
// itemized at any resolution, so limiting the floor never hides a violation.
export function recommendedResolution(unit: string | undefined): number {
  switch (unit) {
    case "months":
      return 30;
    case "weeks":
      return 10;
    case "hours":
    case "days":
    default:
      return 1;
  }
}
